/**
 * Deterministic whole-screen re-theme (restyle_target). Instead of asking the
 * Designer LLM to enumerate every component (which covered 5-19 of 38 components
 * inconsistently), a palette of semantic role colors is projected onto EVERY
 * component by its type → role bucket. Coverage is guaranteed: an unknown type
 * falls to a safe default (text color only), never skipped, never a blind
 * background. Only COLOR fields are written — layout / spacing / size are never
 * touched, which keeps the re-theme safe and reversible.
 *
 * The projection is a PURE function (type + palette → color patch) so coverage
 * is unit-testable without a canvas.
 */

/** Semantic role colors a theme provides. All are CSS color strings. */
export interface ThemePalette {
  /** Page / root background — the darkest (or lightest) base. */
  background: string;
  /** Container surface (panels, cards) — one step off the base. */
  surface: string;
  /** Primary body text. */
  textPrimary: string;
  /** Muted / secondary text. */
  textSecondary: string;
  /** Accent — buttons, active states. */
  primary: string;
  /** Text/icon color that sits ON the accent (contrast). */
  primaryText: string;
  /** Border / divider color. */
  border: string;
}

/** The color fields this re-theme may write. Everything else is left untouched. */
export interface ThemeColorPatch {
  /** background-color (styleObj.fill.color). */
  backgroundColor?: string;
  /** text color (styleObj.text.color). */
  textColor?: string;
  /** border color (styleObj.border.borderColor). */
  borderColor?: string;
}

type ThemeRole = "background" | "surface" | "input" | "button" | "text" | "icon" | "data" | "unknown";

const ROLE_BY_TYPE: Record<string, ThemeRole> = {
  // Root — the page background.
  Body: "background",
  // Containers / surfaces.
  Panel: "surface",
  Card: "surface",
  KCard: "surface",
  Container: "surface",
  Row: "surface",
  Col: "surface",
  Column: "surface",
  VerticalStack: "surface",
  HorizontalStack: "surface",
  Stack: "surface",
  Flex: "surface",
  FlexGrid: "surface",
  ContentSwitcher: "surface",
  Tab: "surface",
  TabItem: "surface",
  Accordion: "surface",
  Collapse: "surface",
  Drawer: "surface",
  Modal: "surface",
  Popover: "surface",
  Header: "surface",
  Footer: "surface",
  // Text.
  Label: "text",
  Title: "text",
  Paragraph: "text",
  Text: "text",
  RichText: "text",
  // Icons.
  Icon: "icon",
  // Inputs — bg + text + border paired.
  TextInput: "input",
  MultiLineTextInput: "input",
  TextArea: "input",
  Email: "input",
  Password: "input",
  Url: "input",
  NumberInput: "input",
  Percent: "input",
  Currency: "input",
  Money: "input",
  SelectBox: "input",
  MultiSelect: "input",
  AutoComplete: "input",
  SingleSelect: "input",
  Date: "input",
  DateRange: "input",
  Datetime: "input",
  DateTimePicker: "input",
  DateTimeRange: "input",
  DatePicker: "input",
  MonthPicker: "input",
  YearPicker: "input",
  TimePicker: "input",
  CheckBox: "input",
  Checkbox: "input",
  CheckboxGroup: "input",
  Switch: "input",
  Toggle: "input",
  RadioGroup: "input",
  RadioButton: "input",
  RadioButtonGroup: "input",
  Radio: "input",
  Slider: "input",
  RangeSlider: "input",
  Rating: "input",
  ColorPicker: "input",
  Upload: "input",
  ImageUpload: "input",
  DocumentUploader: "input",
  // Buttons — accent bg + contrast text paired.
  Button: "button",
  IconButton: "button",
  FloatButton: "button",
  // Data widgets — surface bg + text.
  Table: "data",
  DataTable: "data",
  GalleryView: "data",
  Gallery: "data",
  KanbanBoard: "data",
  Kanban: "data",
  Calendar: "data",
  PivotTable: "data",
  HorizontalTable: "data",
  NodeTree: "data",
  AreaChart: "data",
  BarChart: "data",
  DonutChart: "data",
  GaugeChart: "data",
  LineChart: "data",
  MixedChart: "data",
  PieChart: "data",
  RadarChart: "data",
  RadialChart: "data",
  Chart: "data"
};

/**
 * Root section kuikaIds. The body root's `item.name` is often "Unknown" (not
 * "Body"), so keying the role purely on the type name misses the PAGE background
 * — the most visible surface. Force it by id.
 */
const ROOT_BACKGROUND_IDS: ReadonlySet<string> = new Set<string>(["-1"]);
const ROOT_SURFACE_IDS: ReadonlySet<string> = new Set<string>(["-2", "-3"]);

/**
 * PURE — the role bucket for a component. Root sections are keyed by id (the body
 * root is the page background even when its type name is "Unknown"); everything
 * else by type. Unknown types get "unknown".
 */
export function themeRoleForType(componentType: string, kuikaId?: string): ThemeRole {
  if (kuikaId !== undefined) {
    if (ROOT_BACKGROUND_IDS.has(kuikaId)) return "background";
    if (ROOT_SURFACE_IDS.has(kuikaId)) return "surface";
  }
  return ROLE_BY_TYPE[componentType] ?? "unknown";
}

/**
 * PURE — the color patch for a component of `componentType` under `palette`.
 * bg + text are set together per role (never independently) so an input can
 * never end up light-text-on-light-surface. Unknown types get text color ONLY
 * (a blind background would paint stray boxes on decorative wrappers).
 */
export function themeColorPatchForType(
  componentType: string,
  palette: ThemePalette,
  kuikaId?: string
): ThemeColorPatch {
  const role: ThemeRole = themeRoleForType(componentType, kuikaId);
  switch (role) {
    case "background":
      return { backgroundColor: palette.background };
    case "surface":
      return { backgroundColor: palette.surface };
    case "input":
      return { backgroundColor: palette.surface, textColor: palette.textPrimary, borderColor: palette.border };
    case "button":
      return { backgroundColor: palette.primary, textColor: palette.primaryText };
    case "data":
      return { backgroundColor: palette.surface, textColor: palette.textPrimary, borderColor: palette.border };
    case "text":
      return { textColor: palette.textPrimary };
    case "icon":
      return { textColor: palette.textSecondary };
    case "unknown":
    default:
      // Safe default — recolor text, never blind-paint a background.
      return { textColor: palette.textPrimary };
  }
}

/** Built-in palettes for the two universal cases (no LLM needed). */
export const BUILTIN_THEME_PALETTES: Record<"dark" | "light", ThemePalette> = {
  dark: {
    background: "#0B1220",
    surface: "#111827",
    textPrimary: "#F8FAFC",
    textSecondary: "#94A3B8",
    primary: "#2563EB",
    primaryText: "#F8FAFC",
    border: "#25324A"
  },
  light: {
    background: "#F8FAFC",
    surface: "#FFFFFF",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    primary: "#2563EB",
    primaryText: "#FFFFFF",
    border: "#E2E8F0"
  }
};
